import { Button, InputAdornment, TextField } from '@mui/material';
import DeleteUserDialog from 'components/DeleteUserDialog';
import Dropdown, { DropdownItem } from 'components/Dropdown/Dropdown';
import FileUpload from 'components/FileUpload/FileUpload';
import Header from 'components/Header/Header';
import HeaderIconLogo from 'components/Header/header-items/HeaderIconLogo';
import BlockTitle from 'components/layout/BlockTitle/BlockTitle';
import Loader from 'components/Loader/Loader';
import { LocationSelector } from 'components/ProductEdit/LocationSelector/LocationSelector';
import Tooltip from 'components/Tooltip/Tooltip';
import withAuth from 'components/with-auth/with-auth';
import { Locale } from 'config';
import { useLanguage } from 'hooks/useLanguage';
import Router from 'next/router';
import React, { createRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IosImageOutline from 'react-ionicons/lib/IosImageOutline';
import IosInformationCircleOutline from 'react-ionicons/lib/IosInformationCircleOutline';
import IosLinkOutline from 'react-ionicons/lib/IosLinkOutline';
import { useSelector } from 'react-redux';
import CountryService, { ApiCountry } from 'services/country.service';
import DeliveryService from 'services/delivery.service';
import ShopService from 'services/shop.service';
import UserService from 'services/user.service';
import { setUser } from 'store/actions/user.actions';
import { selectUser } from 'store/selectors/user';
import { logOut } from 'utils/authentication';
import { isValidCountry, isValidLanguage } from 'utils/country-language-mapping';
import { copyToClipboard } from 'utils/dom-element-helpers';
import { notification, useNotification } from 'utils/snackbar';
import validate from 'utils/validate';

const userService = new UserService();
const shopService = new ShopService();
const deliveryService = new DeliveryService();
const countryService = new CountryService();

const MAX_SHOP_NAME_LENGTH = 50;

const MyProfilePage = () => {
  const user = useSelector(selectUser);
  const { t } = useTranslation();
  const shopSlugPrefixRef = createRef<HTMLInputElement>();
  const shopInstagramRef = createRef<HTMLInputElement>();

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [editedUser, setEditedUser] = useState(user);
  const [profileImage, setProfileImage] = useState(null);
  const [isSaving, setSaving] = useState(false);
  const [availableCountries, setAvailableCountries] = useState<ApiCountry[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<DropdownItem>();
  const [openedUserDelete, setOpenedUserDelete] = useState<boolean>(false);
  const [deleteReasons, setDeleteReasons] = useState([]);

  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { setLanguage } = useLanguage();

  const provinces = deliveryService.getProvinces();

  const loadDeleteReasons = async () => {
    const loadedReasons = await userService.getDeleteReasons();

    if (loadedReasons) {
      setDeleteReasons(loadedReasons);
    }
  };

  const fetchUser = async () => {
    const userData = await userService.getCurrentUser();

    setUser({ ...user, ...userData });
    setEditedUser((prev) => ({
      ...prev,
      ...userData,
      shop: { ...prev?.shop, ...userData?.shop },
    }));
  };

  const fetchCountries = async () => {
    try {
      const response = await countryService.getAvailableCountries();

      setAvailableCountries(response?.countries || []);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  useEffect(() => {
    fetchUser();
    fetchCountries();
    loadDeleteReasons();
  }, []);

  // Set selected country when user and countries are available
  useEffect(() => {
    if (user?.country && availableCountries.length > 0) {
      const userCountry = availableCountries.find((country) => country.code === user.country);

      if (userCountry) {
        setSelectedCountry({
          id: availableCountries.findIndex((country) => country.code === user.country),
          title: t(`countries.${userCountry.code}`),
          value: userCountry.code,
        });
      }
    }
  }, [user?.country, availableCountries, t]);

  const uploadImage = async () => {
    if (!profileImage) {
      return;
    }

    const formData = new FormData();

    formData.append('image', profileImage);

    try {
      await userService.updateUserProfileImage(formData, { silentFail: true });
      setProfileImage(null);
    } catch (error) {
      setProfileImage(null);
      showErrorNotification({
        message: t('pages.settings.profile.pic-upload-error'),
        error,
      });
    }
  };

  const handleChange = (input: Array<string>, value) => {
    if (input.length > 1) {
      const newUserData = { ...editedUser[input[0]] };

      newUserData[input[1]] = value;

      return setEditedUser({
        ...editedUser,
        [input[0]]: newUserData,
      });
    }

    return setEditedUser({
      ...editedUser,
      [input[0]]: value,
    });
  };

  const saveSettings = async () => {
    // Validate
    const validation = validate(editedUser, {
      'shop.name': {
        presence: {
          allowEmpty: false,
          message: t('pages.settings.profile.invalid-shop-name'),
        },
        length: {
          maximum: MAX_SHOP_NAME_LENGTH,
        },
      },
      'shop.activeSlug': {
        presence: {
          allowEmpty: false,
          message: t('pages.settings.profile.invalid-active-slug'),
        },
      },
      email: {
        email: {
          message: t('pages.settings.profile.invalid-email'),
        },
      },
    });

    // Catch errors
    if (validation.hasErrors) {
      setErrors(validation?.errors);

      return;
    }

    try {
      setSaving(true);
      await uploadImage();

      // Handle country change separately with proper language validation
      const countryChangeSuccess = editedUser.country !== user.country ? await handleCountryChange(editedUser.country) : true;

      if (!countryChangeSuccess) {
        setSaving(false);

        return; // Error already shown in handleCountryChange
      }

      // Handle other profile updates.
      try {
        await shopService.updateShop(
          editedUser.shop.id,
          {
            name: editedUser.shop.name,
            description: editedUser.shop.description,
            province: editedUser.shop.province,
            location: editedUser.shop.location,
            instagramTag: editedUser.shop.instagramTag,
          },
          { silentFail: true },
        );
      } catch (error) {
        setSaving(false);
        if (error?.data?.data?.errorCode === 'VALIDATION_ERROR' || error?.data?.data?.errorCode === 'NAME_VALIDATION_REJECTED') {
          setErrors({ ...errors, 'shop.name': error?.data?.data?.message });

          return showErrorNotification({ message: error?.data?.data?.message, error });
        }

        return showErrorNotification({ message: t('pages.settings.saving-error'), error });
      }

      try {
        await shopService.updateShopSlug(editedUser.shop.id, editedUser.shop.activeSlug, { silentFail: true });
      } catch (error) {
        setSaving(false);
        if (error?.data?.data?.errorCode === 'INVALID_SLUG_ERROR' || error?.data?.data?.errorCode === 'NAME_VALIDATION_REJECTED') {
          setErrors({ ...errors, 'shop.activeSlug': error?.data?.data?.message });

          return showErrorNotification({ error });
        }

        return showErrorNotification({ message: t('pages.settings.saving-error'), error });
      }

      // Refresh user data to ensure UI is in sync
      await fetchUser();
      setSaving(false);

      // Only show general success message if country didn't change
      // (country change shows its own success message)
      if (editedUser.country === user.country) {
        return showSuccessNotification(t('pages.settings.successfully-saved'));
      }
    } catch (error) {
      setSaving(false);
      if (error?.data?.data?.errorCode === 'USER_DUPLICATE_EMAIL_ERROR') {
        setErrors({
          ...errors,
          email: t('pages.settings.profile.duplicate-email'),
        });

        return showErrorNotification({
          message: t('pages.settings.profile.duplicate-email'),
          error,
        });
      }

      return showErrorNotification({
        message: t('pages.settings.saving-error'),
        error,
      });
    }
  };

  const toggleHolidayMode = async () => {
    try {
      await shopService.updateShop(user.shop.id, { holidayMode: !user.shop.holidayMode }, { silentFail: true }).then((response) => {
        user.shop.holidayMode = response.holiday_mode;
        setUser({ ...user });

        notification.showSuccessNotification(t(`pages.settings.profile.holiday-mode.${response.holiday_mode ? 'enabled' : 'disabled'}`));
      });
    } catch (err) {
      if (err?.data?.data?.type === 'has-pending-orders') {
        notification.showInfoNotification(t('pages.settings.profile.holiday-mode.unshipped-orders'));
      }
    }
  };

  /**
   * Handle country change with proper language validation and updates
   * This function lets the backend handle country + language logic atomically
   */
  const handleCountryChange = async (newCountry: string): Promise<boolean> => {
    // Validate country
    if (!isValidCountry(newCountry)) {
      console.error(`Invalid country provided: ${newCountry}`);
      showErrorNotification({
        error: new Error(t('pages.settings.profile.invalid-country')),
      });

      return false;
    }

    const previousLanguage = user.language;
    const previousCountry = user.country;

    try {
      // Let backend handle country change and language adjustment atomically
      const updatedUser = await userService.updateUser({ country: newCountry });

      if (!updatedUser) {
        throw new Error('Failed to get updated user data');
      }

      // Validate returned language and country
      if (!isValidLanguage(updatedUser.language) || !isValidCountry(updatedUser.country)) {
        console.error('Backend returned invalid language/country:', updatedUser.language, updatedUser.country);
        throw new Error('Invalid language/country returned from backend');
      }

      // Update Redux store with backend's authoritative response
      setUser({ ...user, ...updatedUser });

      // Check if backend changed the language due to country change
      const languageChanged = updatedUser.language !== previousLanguage;
      const countryChanged = updatedUser.country !== previousCountry;

      if (languageChanged) {
        // Update frontend i18n system to match backend decision
        const newLocale = `${updatedUser.language}-${updatedUser.country}` as Locale;

        try {
          await setLanguage({ locale: newLocale, saveToDb: false });

          // Show notification about language change
          showSuccessNotification(t('pages.settings.profile.language-changed-due-to-country'));

          // Soft reload to apply language changes
          Router.replace(Router.asPath);
        } catch (i18nError) {
          console.error('Failed to update i18n after country change:', i18nError);
          // Don't fail the whole operation for i18n issues
        }
      } else if (countryChanged) {
        // Country changed but language remained the same
        showSuccessNotification(
          t('pages.settings.profile.country-updated-successfully', {
            country: t(`countries.${updatedUser.country}`),
          }),
        );
      }

      return true;
    } catch (error) {
      console.error('Country change failed:', error);

      // Revert UI state
      setEditedUser({ ...editedUser, country: previousCountry });

      // Show appropriate error message
      if (error?.data?.data?.code === 'COUNTRY_NOT_SUPPORTED') {
        showErrorNotification({
          message: t('pages.settings.profile.country-not-supported'),
          error,
        });
      } else if (error?.data?.data?.code === 'VALIDATION_ERROR') {
        showErrorNotification({
          message: t('pages.settings.profile.invalid-country'),
          error,
        });
      } else {
        showErrorNotification({
          message: t('pages.settings.profile.country-change-error'),
          error,
        });
      }

      return false;
    }
  };

  return (
    <>
      <Header>
        <HeaderIconLogo icon="back" showLogo={false} />
      </Header>

      <div className="settings-detail-page settings-profile-page mobile-full-width">
        <BlockTitle mt={0} className="settings-title">
          <img src="/icons/icn-profile.svg" className="settings-title-icon" alt="profile" />
          {t('pages.settings.profile.title')}
        </BlockTitle>

        {isSaving ? (
          <Loader />
        ) : (
          <>
            {/* SHOP PHOTO */}
            <div className="image">
              <div className="image-upload">
                <FileUpload
                  onChange={(files) => {
                    if (files.length > 0) {
                      setProfileImage(files[0]);
                    }
                  }}
                  preview={true}
                  initialImage={user.profile_picture?.original || user?.picture}
                  size={100}
                  inputClassName="profile-change-image-upload"
                />
              </div>

              {/* CHANGE IMAGE */}
              <Button
                size="small"
                variant="outlined"
                color="primary"
                className="change-profile-image-btn"
                onClick={() => {
                  if (typeof document !== 'undefined') {
                    const imageUpload: HTMLElement = document.querySelector('.profile-change-image-upload');

                    imageUpload.click();
                  }
                }}
              >
                <IosImageOutline className="change-img-btn-icon" />
                {t('pages.settings.profile.change-picture')}
              </Button>

              {/* LOGOUT */}
              <Button onClick={() => logOut()} size="small" variant="outlined" color="primary" className="logout-btn">
                {t('common.log-out')}
              </Button>
            </div>

            <BlockTitle>{t('pages.settings.profile.info')}</BlockTitle>

            {/* SHOP NAME */}
            <TextField
              variant="standard"
              id="shop-name"
              error={!!errors?.['shop.name']}
              className="mt-0-i"
              label={t('pages.settings.profile.shop-name')}
              value={editedUser?.shop?.name}
              placeholder=""
              inputProps={{
                maxLength: MAX_SHOP_NAME_LENGTH,
              }}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(event) => {
                setErrors({
                  ...errors,
                  'shop.name': null,
                });
                handleChange(['shop', 'name'], event.target.value);
              }}
              margin="normal"
              helperText={errors?.['shop.name']}
            />

            {/* SHOP ADDRESS */}
            <TextField
              variant="standard"
              label={t('pages.settings.profile.shop-address')}
              error={!!errors?.['shop.activeSlug']}
              value={editedUser?.shop?.activeSlug}
              margin="normal"
              onChange={(event) => {
                setErrors({
                  ...errors,
                  'shop.activeSlug': null,
                });
                handleChange(['shop', 'activeSlug'], event.target.value);
              }}
              inputRef={shopSlugPrefixRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" className="shop-url-prefix mr-0-i" onClick={() => shopSlugPrefixRef?.current?.focus()}>
                    {window.location.origin + '/'}
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment
                    position="end"
                    className="shop-url-copy-btn"
                    onClick={() => {
                      copyToClipboard(window.location.origin + '/' + editedUser?.shop?.activeSlug);
                      showSuccessNotification(t('common.link-copied'));
                    }}
                  >
                    <IosLinkOutline />
                  </InputAdornment>
                ),
              }}
              helperText={errors?.['shop.activeSlug']}
            />

            {/* INSTAGRAM ADDRESS */}
            <TextField
              variant="standard"
              label={t('pages.settings.profile.instagram')}
              error={!!errors?.['shop.instagramTag']}
              value={editedUser?.shop?.instagramTag}
              margin="normal"
              onChange={(event) => {
                setErrors({
                  ...errors,
                  'shop.instagramTag': null,
                });

                // remove spaces from the instagram tag
                const newValue = event.target.value.trim().replace(/\s+/g, '').toLowerCase();

                handleChange(['shop', 'instagramTag'], newValue);
              }}
              inputRef={shopInstagramRef}
              placeholder={t('pages.settings.profile.instagramPlaceholder')}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    className="instagram-url-prefix mr-0-i"
                    onClick={() => shopInstagramRef?.current?.focus()}
                  >
                    https://instagram.com/
                  </InputAdornment>
                ),
              }}
              helperText={errors?.['shop.instagramTag']}
            />

            <LocationSelector
              provinces={provinces}
              province={editedUser?.shop?.province}
              city={editedUser?.shop?.location ?? ''}
              onProvinceChanged={(p) => handleChange(['shop', 'province'], p)}
              onCityChanged={(c) => handleChange(['shop', 'location'], c)}
            />

            {/* DESCRIPTION */}
            <TextField
              variant="standard"
              id="description"
              value={editedUser?.shop?.description || ''}
              multiline={true}
              onChange={(event) => handleChange(['shop', 'description'], event.target.value)}
              margin="normal"
              inputProps={{
                maxLength: 400,
              }}
              placeholder=""
              InputLabelProps={{
                shrink: true,
              }}
              label={t('pages.settings.profile.description')}
            />

            {/* EMAIL */}
            <TextField
              disabled
              variant="standard"
              id="email"
              error={!!errors?.email}
              value={editedUser?.email || ''}
              margin="normal"
              type="email"
              onChange={(event) => {
                setErrors({
                  ...errors,
                  email: null,
                });
                handleChange(['email'], event.target.value);
              }}
              placeholder=""
              InputProps={{
                endAdornment: (
                  <Tooltip title={t('pages.settings.profile.email-tooltip')}>
                    <IosInformationCircleOutline className="small-icon margin-bottom-1px" fontSize={'19px'} />
                  </Tooltip>
                ),
              }}
              InputLabelProps={{
                shrink: true,
              }}
              label={t('pages.settings.profile.email')}
              helperText={errors?.email}
            />

            {/* MARKET COUNTRY */}
            {availableCountries.length > 1 && (
              <Dropdown
                className="mt-2 mb-1"
                selectedItem={selectedCountry}
                label={t('components.onboarding.user-settings.country-label')}
                items={availableCountries.map((country, index) => ({
                  id: index,
                  title: t(`countries.${country.code}`),
                  value: country.code,
                }))}
                onSelect={(item) => {
                  setSelectedCountry(item);
                  handleChange(['country'], item.value);
                }}
                disableAnimation={true}
              />
            )}

            <Button size="large" variant="contained" color="primary" onClick={saveSettings} className="profile-save-btn">
              {t('pages.settings.save-changes')}
            </Button>

            {/* HOLIDAY MODE */}
            <BlockTitle>{t('pages.settings.profile.holiday-mode.title')}</BlockTitle>

            {!user.shop.holidayMode && (
              <div>
                <p className="text-soft mt-0">{t('pages.settings.profile.holiday-mode.explanation-1')}</p>

                <Button onClick={toggleHolidayMode} size="large" variant="outlined" color="primary" className="profile__holiday-mode-btn">
                  {t('pages.settings.profile.holiday-mode.enable')}
                </Button>
              </div>
            )}

            {user.shop.holidayMode && (
              <div>
                <p className="text-soft mt-0">{t('pages.settings.profile.holiday-mode.explanation-2')}</p>

                <Button onClick={toggleHolidayMode} size="large" variant="outlined" color="primary" className="profile__holiday-mode-btn">
                  {t('pages.settings.profile.holiday-mode.disable')}
                </Button>
              </div>
            )}

            <BlockTitle>{t('pages.settings.profile.delete.delete-account')}</BlockTitle>

            <p className="text-soft mt-0">{t('pages.settings.profile.delete.explanation')}</p>

            <Button size="large" variant="contained" onClick={() => setOpenedUserDelete(true)} className="profile__delete-btn">
              {t('pages.settings.profile.delete.delete-account')}
            </Button>

            {openedUserDelete && (
              <DeleteUserDialog opened={openedUserDelete} close={() => setOpenedUserDelete(false)} deleteReasons={deleteReasons} />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default withAuth(MyProfilePage);
